import { useState } from "react";
import { DropResult } from "@hello-pangea/dnd";
import { useParams } from "react-router";
import { useTranslation } from "../../localization/useTranslation";
import { put } from "../../common/api/apiShared.ts";
import { EntityTemplateStep, getEntityTemplateTranslationKeys, TemplateEntityType } from "../EntityTemplate.types.ts";
import { useUrlProvider } from "../../useUrlProvider.ts";
import { EntityTemplateStepsViewModel } from "./EntityTemplateSteps.types.ts";
import { useDotLegalQuery, useDotLegalQueryClient, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useEntityTemplateSteps() {
    const queryClient = useDotLegalQueryClient();
    const urlProvider = useUrlProvider();

    const { id } = useParams<{ id: string }>();
    const { translateString } = useTranslation();

    const [stepToDelete, setStepToDelete] = useState<EntityTemplateStep | undefined>(undefined);
    const [stepToEdit, setStepToEdit] = useState<EntityTemplateStep | undefined>(undefined);
    const [showAddStepDialog, setShowAddStepDialog] = useState(false);
    const [steps, setSteps] = useState<Array<EntityTemplateStep>>([]);

    const url = `/entity-templates/${id}/steps`;
    const { isLoading, data, refetch } = useDotLegalQuery<EntityTemplateStepsViewModel>(url);

    const reorderMutation = useDotLegalMutation(updateStepOrderApi);
    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }
        const sourceIndex = result.source.index;
        const destinationIndex = result.destination.index;

        if (sourceIndex === destinationIndex) {
            return;
        }

        const sourceItem = data!.steps[sourceIndex];
        const destinationItem = data!.steps[destinationIndex];

        let tempData = { ...data! };
        const newSteps = reOrderSteps(tempData.steps, sourceItem.id, destinationItem.id);

        // The `setSteps` call is actually serving as a state synchronization mechanism. It ensures that - The component has immediate access to the reordered data
        setSteps(newSteps);

        tempData.steps = newSteps;
        queryClient.setQueryData(url, tempData);
        reorderMutation.mutateAsync(
            { stepId: sourceItem.id, toStepId: destinationItem.id },
            {
                onSuccess: () => {},
            }
        );
    };

    const editStep = (step: EntityTemplateStep) => {
        setStepToEdit(step);
    };

    const onCloseCreateEditStepDialog = () => {
        setShowAddStepDialog(false);
        setStepToEdit(undefined);
    };

    const onStepSaved = () => {
        refetch();
        setShowAddStepDialog(false);
        setStepToEdit(undefined);
    };

    const onStepDeleted = () => {
        refetch();
        setStepToDelete(undefined);
    };

    const getBreadCrumbTemplateTypeName = () => {
        if (data?.templateType) {
            return translateString(getEntityTemplateTranslationKeys(data.templateType).templates);
        }

        return "";
    };

    const breadCrumbs = [
        { name: translateString("settings"), link: urlProvider.getSettingsUrl() },
        { name: getBreadCrumbTemplateTypeName(), link: urlProvider.getEntityTemplates(data?.templateType ?? TemplateEntityType.Contract) },
        { name: data?.name, link: urlProvider.getEditEntityTemplate(id!) },
        { name: translateString("manageSteps") },
    ];

    return {
        id,
        isLoading,
        isStandardTemplate: !!data?.isStandardTemplate,
        steps: data?.steps,
        breadCrumbs,
        handleDragEnd,
        stepToDelete,
        setStepToDelete,
        onStepDeleted,
        editStep,
        stepToEdit,
        onCloseCreateEditStepDialog,
        showAddStepDialog,
        onStepSaved,
        setShowAddStepDialog,
    };

    function reOrderSteps(steps: Array<EntityTemplateStep>, sourceId: string, destinationId: string) {
        const sourceIndex = steps.findIndex((x) => x.id === sourceId);
        const destinationIndex = steps.findIndex((x) => x.id === destinationId);

        const [fromQuestion] = steps.splice(sourceIndex, 1);
        steps.splice(destinationIndex, 0, fromQuestion);

        steps.forEach((x, i) => {
            x.order = i + 1;
        });

        return steps;
    }

    async function updateStepOrderApi(data: { stepId: string; toStepId: string }) {
        return await put(`/entity-templates/${id!}/steps/update-order`, {
            entityTemplateId: id,
            stepId: data.stepId,
            toStepId: data.toStepId,
        });
    }
}
